import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CookieUtil } from './cookies';

@Injectable({
  providedIn: 'root',
})
export class AcquisitionChannelUtil {
  private readonly KEY_COOKIE = 'acq_chn';

  constructor(
    private cookies: CookieUtil,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Detecta y guarda el canal de adquisición si no existe uno previo
   * @returns Canal de adquisición detectado o existente
   */
  detectAndSave(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return 'unknown';
    }

    const existingChannel = this.getExistingChannel();
    if (existingChannel) {
      return existingChannel;
    }

    const detectedChannel = this.detectChannel();
    this.saveChannel(detectedChannel);
    return detectedChannel;
  }

  /**
   * Obtiene el canal de adquisición existente desde cookie
   */
  private getExistingChannel() {
    try {
      const stored = this.cookies.get(this.KEY_COOKIE);
      return stored;  
    } catch {
      return '';
    }
  }

  /**
   * Guarda el canal de adquisición en cookie
   */
  private saveChannel(data: string): void {
    try {
      this.cookies.save(this.KEY_COOKIE, data, 7);
    } catch {}
  }

  /**
   * Detecta el canal de adquisición basado en referrer y user agent
   */
  private detectChannel(): string {
    const referrer = document.referrer?.toLowerCase() || '';
    const userAgent = navigator.userAgent?.toLowerCase() || '';
    const currentUrl = window.location.href?.toLowerCase() || '';

    if (referrer) {
      try {
        const referrerUrl = new URL(referrer);
        const domain = referrerUrl.hostname.toLowerCase();


        const cleanDomain = domain
          .replace(/^(www\.|m\.|i\.|mobile\.|app\.|web\.)/, '')
          .replace(/^(es\.|en\.|mx\.|co\.|ar\.)/, '');

        const domainMapping: { [key: string]: string } = {
          'instagram.com': 'instagram.com',
          'facebook.com': 'facebook.com',
          'fb.com': 'facebook.com',
          'whatsapp.com': 'whatsapp.com',
          'twitter.com': 'twitter.com',
          'x.com': 'twitter.com',
          'tiktok.com': 'tiktok.com',
          'linkedin.com': 'linkedin.com',
          'youtube.com': 'youtube.com',
          'youtu.be': 'youtube.com',
          'snapchat.com': 'snapchat.com',
          'telegram.org': 'telegram.org',
          'telegram.me': 'telegram.org',

          // Buscadores
          'google.com': 'google.com',
          'google.com.co': 'google.com',
          'bing.com': 'bing.com',
          'yahoo.com': 'yahoo.com',

          // Medios colombianos
          'eltiempo.com': 'eltiempo.com',
          'semana.com': 'semana.com',
          'pulzo.com': 'pulzo.com',
          'caracoltv.com': 'caracoltv.com',
          'rcnradio.com': 'rcnradio.com',
          'elespectador.com': 'elespectador.com',
          'larepublica.co': 'larepublica.co',
          'portafolio.co': 'portafolio.co',
          'dinero.com': 'dinero.com',

          // Plataformas de contenido
          'reddit.com': 'reddit.com',
          'pinterest.com': 'pinterest.com',
          'tumblr.com': 'tumblr.com',
          'twitch.tv': 'twitch.tv',
        };

        const normalizedDomain = domainMapping[cleanDomain] || cleanDomain;
        return normalizedDomain;
      } catch (error) {
        const domain = referrer.split('/')[2] || referrer;
        return `${domain.toLowerCase()}`;
      }
    }

    const ua = userAgent;

    if (ua.includes('whatsapp')) {
      return 'whatsapp.com';
    }

    // 2. WhatsApp Android WebView Patterns (más específicos) - RETORNA SOLO DOMINIO
    if (ua.includes('wv') && ua.includes('version/') && !referrer) {
      if (ua.includes('mobile') && ua.includes('android')) {
        return 'whatsapp.com';
      }
    }

    // 4. WhatsApp iOS WebView Patterns - RETORNA SOLO DOMINIO
    if (ua.includes('version/') && ua.includes('mobile/') && ua.includes('safari/') && !referrer) {
      if (ua.includes('iphone') || ua.includes('ipad')) {
        return 'whatsapp.com';
      }
    }

    // 5. Patrones generales de WebView sin referrer (alta probabilidad WhatsApp) - RETORNA SOLO DOMINIO
    if (!referrer && ua.includes('wv') && ua.includes('version/')) {
      return 'whatsapp.com';
    }

    // Instagram In-App Browser (patrones específicos) - RETORNA SOLO DOMINIO
    if (ua.includes('instagram') || ua.includes('ig_') || (ua.includes('fbav') && ua.includes('instagram'))) {
      return 'instagram.com';
    }

    // Facebook In-App Browser - RETORNA SOLO DOMINIO
    if (ua.includes('fban') || ua.includes('fbav') || ua.includes('facebook')) {
      return 'facebook.com';
    }

    // TikTok In-App Browser - RETORNA SOLO DOMINIO
    if (ua.includes('tiktok') || ua.includes('musically') || ua.includes('trill')) {
      return 'tiktok.com';
    }

    // YouTube In-App (desde app móvil) - RETORNA SOLO DOMINIO
    if (ua.includes('youtube') || (ua.includes('google') && ua.includes('mobile') && ua.includes('version/'))) {
      return 'youtube.com';
    }

    // Twitter/X In-App Browser - RETORNA SOLO DOMINIO
    if (ua.includes('twitter') || ua.includes('twitterandroid')) {
      return 'twitter.com';
    }

    // LinkedIn In-App Browser - RETORNA SOLO DOMINIO
    if (ua.includes('linkedin')) {
      return 'linkedin.com';
    }

    // Telegram In-App Browser - RETORNA SOLO DOMINIO
    if (ua.includes('telegram') || ua.includes('tgwebapp')) {
      return 'telegram.org';
    }

    // Snapchat In-App Browser - RETORNA SOLO DOMINIO
    if (ua.includes('snapchat')) {
      return 'snapchat.com';
    }

    // Google Apps/Services (suelen no enviar referrer por seguridad) - RETORNA SOLO DOMINIOS
    if (ua.includes('googlechat') || ua.includes('google chat')) return 'google.com';
    if (ua.includes('gmail') && ua.includes('mobile')) return 'google.com';
    if (ua.includes('google') && (ua.includes('workspace') || ua.includes('gsuite'))) return 'google.com';
    if (ua.includes('gsa/')) return 'google.com';

    // Messaging/Collaboration Apps (no envían referrer por seguridad) - RETORNA SOLO DOMINIOS
    if (ua.includes('slack')) return 'slack.com';
    if (ua.includes('teams') || ua.includes('msteams')) return 'microsoft.com';
    if (ua.includes('discord')) return 'discord.com';
    if (ua.includes('telegram')) return 'telegram.org';

    // 3. Análisis de características del navegador/entorno
    const isChrome = ua.includes('chrome');
    const isMobile = ua.includes('mobile');
    const isAndroid = ua.includes('android');
    const isIOS = ua.includes('iphone') || ua.includes('ipad');

    // Chrome en Android (posible desde Google Chat/Workspace) - RETORNA SOLO DOMINIO
    if (isChrome && isAndroid && !referrer) {
      return 'google.com';
    }

    // 4. Analizar la URL actual por parámetros específicos - RETORNA SOLO DOMINIOS
    if (currentUrl.includes('fbclid=')) return 'facebook.com';
    if (currentUrl.includes('gclid=')) return 'google.com';
    if (currentUrl.includes('msclkid=')) return 'microsoft.com';
    if (currentUrl.includes('ttclid=')) return 'tiktok.com';
    if (currentUrl.includes('igshid=')) return 'instagram.com';

    // 5. Heurísticas para contexto de desarrollo vs producción
    const hostname = window.location.hostname;
    const isLocalhost = hostname.includes('localhost') || hostname === '127.0.0.1';

    if (isLocalhost && !referrer) {
      return 'development-testing';
    }

    // 6. Análisis de tráfico directo vs posible app empresarial
    if (!referrer || referrer === '') {
      // ALTA PROBABILIDAD WHATSAPP: WebView móvil sin referrer - RETORNA SOLO DOMINIO
      if (isMobile && ua.includes('wv') && ua.includes('version/')) {
        return 'whatsapp.com';
      }

      // WHATSAPP iOS: Safari Mobile + Version sin referrer - RETORNA SOLO DOMINIO
      if (isIOS && ua.includes('version/') && ua.includes('mobile/') && ua.includes('safari/')) {
        return 'whatsapp.com';
      }

      // Fallbacks originales
      if (isMobile) {
        return 'direct-mobile';
      }

      return 'direct-desktop';
    }

    // 7. Default para casos no identificados
    return 'none';
  }

  /**
   * Obtiene el canal de adquisición actual
   */
  getCurrentChannel(): string | null {
    const data = this.getExistingChannel();
    return data;
  }

  /**
   * Obtiene toda la información del canal de adquisición
   */
  getChannelData(): string | null {
    return this.getExistingChannel();
  }

  /**
   * Limpia el canal de adquisición guardado (para testing o reset)
   */
  clearChannel(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        this.cookies.delete(this.KEY_COOKIE);
      } catch {}
    }
  }
}
